import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useUserColor } from "@/components/hooks/useUserColor";

export function PageHeader({ title, subtitle, actions, testid }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-testid={testid}>
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-16 text-center">
      {Icon && (
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-xs">
          <Icon className="h-6 w-6 text-sky-500" />
        </div>
      )}
      <h3 className="font-heading text-lg font-semibold text-slate-800">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Loading({ className }) {
  return (
    <div className={cn("flex items-center justify-center py-20 text-slate-400", className)}>
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );
}

export function Card({ className, children, ...props }) {
  return (
    <div
      className={cn("rounded-xl border border-slate-200/80 bg-white shadow-xs", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function Avatar({ name, className, color, user }) {
  const lookup = useUserColor();
  const c = color || (user ? lookup(name) : null);
  const init = (name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div style={c ? { backgroundColor: c, color: "#fff" } : undefined} className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-xs font-bold text-sky-700", className)}>
      {init}
    </div>
  );
}

export function AssigneeAvatars({ names = [], max = 3, className }) {
  const list = (names || []).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <div className={cn("flex items-center", className)} data-testid="assignee-avatars">
      {list.slice(0, max).map((n, i) => (
        <Avatar key={i} name={n} user className="-ml-1.5 h-7 w-7 text-[10px] ring-2 ring-white first:ml-0" />
      ))}
      {list.length > max && <span className="ml-1 text-xs font-medium text-slate-400">+{list.length - max}</span>}
    </div>
  );
}
